/**
 * @file ChatSendInput.tsx
 * @description 聊天输入富文本组件（Slate），支持：
 * - 输入 @ 触发成员/对象建议列表并插入 mention 节点（inline/void）
 * - 通过 ref 暴露方法：移动光标、手动触发 @ 建议
 * - 占位符、禁用态、包含文件时的顶部留白
 * - Enter 发送、方向键越过 mention 整块的交互优化
 * - 自适应建议菜单位置，移动端/小屏适配
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { createEditor, Descendant, Editor, Range, Element as SlateElement, Text, Transforms } from 'slate'
import { Editable, ReactEditor, Slate, useSlate, withReact } from 'slate-react'
import './ChatSendInput.less'
import MentionSuggestions from './components/MentionSuggestions'
// 定义 props 接口
/**
 * ChatSendInput Props
 * - newValue: 外部注入的初始内容（Slate 值）
 * - toolList: 建议工具列表（预留）
 * - placeholder: 占位文案
 * - sendValue: 字符串回显（影响占位符显示）
 * - disabled: 是否禁用
 * - save: 内容变化回调（上报父组件）
 * - enterFun: 回车发送回调
 * - containsFile: 是否包含附件（影响占位符布局）
 * - onInputClick: 输入区域点击回调
 */
interface ChatSendInputProps {
  newValue: Descendant[] | any // 新的编辑器内容
  toolList?: string[] // 工具列表（用于建议）
  placeholder?: string // placeholder 显示
  sendValue?: string // value 输入内容 = 判断显示
  disabled?: boolean // disabled禁用
  save: (newContent: Descendant[]) => void // 保存函数
  enterFun: () => void // enter函数
  containsFile?: boolean // 是否包含文件
  onInputClick?: () => void // 输入框点击时的回调
}

// 定义组件的 ref 接口
/**
 * 对外暴露方法
 * - moveCursorToIndex: 将光标移动到指定字符索引
 * - insertCurlyBracket/insertSquareBracket: 触发 @ 建议（当前统一触发 @）
 */
export interface ChatSendInputRef {
  // 用于移动光标到指定索引
  moveCursorToIndex: (index: number) => void
  /* 点击弹出提示list = 引入手动 */
  insertCurlyBracket: () => void
  insertSquareBracket: () => void
}

/**
 * ChatSendInput 组件
 * 使用 forwardRef 暴露编辑器操作；内部采用 Slate 管理富文本与 mention 节点。
 *
 * @param props
 * @param props.newValue 外部注入的初始 Slate 值（Descendant[]）
 * @param props.toolList 工具列表（预留，供 @ 建议扩展）
 * @param props.save 内容变更回调，返回最新 Slate 值
 * @param props.placeholder 占位文本
 * @param props.disabled 是否只读禁用
 * @param props.enterFun 回车发送时触发
 * @param props.sendValue 字符串回显（影响占位符显示逻辑）
 * @param props.containsFile 是否包含文件（影响顶部留白）
 * @param props.onInputClick 输入区域点击回调
 * @returns ReactElement（富文本输入区域）
 */
const ChatSendInput = forwardRef<ChatSendInputRef, ChatSendInputProps>(
  ({ newValue, toolList, save, placeholder, disabled, enterFun, sendValue, containsFile, onInputClick }, ref) => {
    // 本地状态，控制placeholder显示类型，默认true
    let [placholderType, setplacholderType] = useState(true)
    // 初始化Slate编辑器实例，使用useMemo避免重复创建
    const editor = useMemo(() => {
      // 创建带React功能的编辑器实例
      const e = withReact(createEditor())
      // 先缓存默认的isVoid和isInline判断函数
      const { isVoid, isInline } = e
      // 扩展isVoid逻辑，如果元素类型是'mention'，则视为void元素（不可编辑，类似inline块）
      e.isVoid = (element: any) => element.type === 'mention' || isVoid(element)
      // 扩展isInline逻辑，如果元素类型是'mention'，则视为inline元素（内联元素）
      e.isInline = (element: any) => element.type === 'mention' || isInline(element)
      return e
    }, [])

    // 编辑器内容的状态，默认是一段空段落，或使用传入的newValue
    const [value, setValue] = useState<Descendant[]>(
      newValue.length ? newValue : [{ type: 'paragraph', children: [{ text: '' }] }]
    )
    // 控制建议弹窗是否打开的状态
    const [open, setOpen] = useState(false)
    // 当前激活的建议类型字符串，如 '@'、'{' 等
    const [suggestionsType, setSuggestionsType] = useState('')
    // 建议菜单的位置，默认为距离顶部35px，左边40px
    const [menuPosition, setMenuPosition] = useState({ top: 35, left: 40 })
    // 保存建议列表中每个条目的ref，方便键盘导航时定位元素
    const suggestionRefs = useRef<(HTMLDivElement | null)[]>([])
    // 标记是否处于键盘导航建议列表状态（上下键切换）
    const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false)
    // 保存最近一次选区范围，用于恢复或判断光标位置
    const latestSelection = useRef<Range | null>(null)
    /* 搜索关键字，用于建议过滤 */
    let [searchName, setSearchName] = useState('')
    //区分输入最后按键
    const lastKeyRef = useRef<string | null>(null)
    // ref暴露给外部调用的方法 = 点击插入{} [] 弹出引用list 点击 = 手动输入
    const editorRef = useRef(editor)
    /**
     * 对外暴露的插入`@`符号方法，调用插入方括号及展示建议弹窗的方法
     */
    /**
     * 对外：插入 '@' 并展开建议
     * @returns void
     */
    const insertCurlyBracket = () => insertBracketAndShowSuggestions('@')
    /**
     * 插入指定的括号符号（这里固定是'@'），并显示建议弹窗
     * @param bracketType - 这里为 '@'
     */
    /**
     * 插入触发符并弹出建议列表
     */
    /**
     * 插入触发符并弹出建议列表
     * @param bracketType 触发字符（当前固定为 '@'）
     */
    const insertBracketAndShowSuggestions = (bracketType: '@') => {
      const editor = editorRef.current
      if (!editor) return
      const { selection } = editor
      if (selection) {
        // 光标存在时，插入@符号文本
        Transforms.insertText(editor, bracketType)
        // 插入后获取光标后面的位置点
        const newPoint = Editor.after(editor, editor.selection!.focus)
        if (newPoint) {
          const pointBefore = { path: newPoint.path, offset: newPoint.offset - 1 }
          Transforms.select(editor, pointBefore)
        }
      } else {
        // 没有光标时，选中文档末尾
        const endPoint = Editor.end(editor, [])
        Transforms.select(editor, endPoint)
        // 插入@符号
        Transforms.insertText(editor, bracketType)
        const newPoint = {
          path: endPoint.path,
          offset: endPoint.offset + bracketType.length
        }
        Transforms.select(editor, newPoint)
      }
      // 聚焦编辑器，确保光标闪烁
      ReactEditor.focus(editor)
      // 清空最后按键标记
      lastKeyRef.current = ''
      // 打开建议弹窗，设置建议类型为@
      setOpen(true)
      setSuggestionsType(bracketType)
      // 重置键盘导航状态，表示刚刚开启建议列表
      setIsKeyboardNavigating(false)
    }

    /* 监听滚动 上下箭头选择list */
    // useEffect(() => {
    //   if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
    //     suggestionRefs.current[selectedIndex]!.scrollIntoView({
    //       block: 'nearest',
    //       behavior: 'smooth'
    //     })
    //   }
    // }, [selectedIndex])

    /* 判断键盘 鼠标 选择变量list */
    useEffect(() => {
      if (isKeyboardNavigating) {
        const timer = setTimeout(() => setIsKeyboardNavigating(false), 500)
        return () => clearTimeout(timer)
      }
    }, [isKeyboardNavigating])

    /* newValue 数据构造后的数据 list[{children:{text:'xxx'}}] */
    useEffect(() => {
      setValue(newValue)
    }, [newValue])

    /* 
    1 字符串判断区分展示ploachoder 
    2 回显数据  = 修改 编辑对话 需要回溯@xxx
    */
    useEffect(() => {
      setplacholderType(sendValue ? false : true)
    }, [sendValue])

    // 实现 moveCursorToIndex 方法，允许外部控制光标跳转到指定索引
    const moveCursorToIndex = (targetIndex: number) => {
      const editor = editorRef.current
      if (!editor) return

      let runningIndex = 0
      const nodes = Array.from(Editor.nodes(editor, { at: [], match: Text.isText }))

      for (let [node, path] of nodes) {
        const textLength = node.text.length
        const nextIndex = runningIndex + textLength
        if (targetIndex <= nextIndex) {
          const offset = targetIndex - runningIndex
          const point = { path, offset }
          Transforms.select(editor, { anchor: point, focus: point })
          ReactEditor.focus(editor)
          latestSelection.current = { anchor: point, focus: point }
          return
        }
        runningIndex = nextIndex
      }
    }

    // 将 moveCursorToIndex 暴露给父组件，允许父组件控制光标
    useImperativeHandle(ref, () => ({
      moveCursorToIndex,
      insertCurlyBracket,
      insertSquareBracket: () => insertBracketAndShowSuggestions('@')
    }))

    // 编辑器内容变化时的处理函数，更新内容并根据输入显示建议菜单
    /**
     * 计算与更新建议弹窗位置（限制不出屏）
     */
    const updateMenuPosition = (rect: DOMRect, suggestionsTypeTransfer: string) => {
      const isSmallScreen = window.innerWidth < 1200
      const menuWidth = isSmallScreen ? 300 : 300
      const menuHeight = isSmallScreen ? 70 : 170
      const variableHeight = isSmallScreen ? 100 : 160
      // 计算 top（你当前设置为菜单始终在输入框上方）
      const top = rect.bottom - menuHeight
      // 初始 left
      let left = rect.left - 13 + window.scrollX
      // === 限制右边不超出屏幕 ===
      const maxAllowedLeft = window.innerWidth - menuWidth + window.scrollX
      if (left + menuWidth > window.innerWidth + window.scrollX) {
        left = maxAllowedLeft
      }
      setMenuPosition({ top, left })
    }

    /* 位置显示 */
    // 兜底处理位置计算异常
    /**
     * 计算与设置建议弹窗位置
     * @param rect 触发点的 DOMRect
     * @param suggestionsTypeTransfer 建议类型标识（'@'）
     */
    const handleMenuPosition = (rect: DOMRect, suggestionsTypeTransfer: string) => {
      try {
        updateMenuPosition(rect, suggestionsTypeTransfer)
        setOpen(true)
      } catch (e) {
        setOpen(false)
      }
    }

    /* change输入事件 */
    /**
     * Slate onChange：
     * - 写回父组件（save）
     * - 折叠选区与最近 @ 触发展示建议
     * - 3 字符搜索上限（同企业微信）
     */
    /**
     * Slate onChange
     * @param newValue 新的 Slate 值
     */
    const handleChange = newValue => {
      /* placholder判断显示 */
      if (disabled) {
        setplacholderType(true)
        return
      }
      /* 赋值 */
      setValue(newValue)
      save(newValue)

      latestSelection.current = editor.selection

      const { selection } = editor
      if (!selection || !Range.isCollapsed(selection)) {
        setOpen(false)
        return
      }
      /* 光标移动时不弹窗 */
      if (lastKeyRef.current === 'ArrowLeft' || lastKeyRef.current === 'ArrowRight') {
        setOpen(false)
        return
      }
      try {
        const [node, path] = Editor.node(editor, selection.anchor)
        if (!Editor.hasPath(editor, path) || !Text.isText(node)) {
          setOpen(false)
          return
        }
        const text = node.text
        const offset = selection.anchor.offset
        // 找到光标前最近的 '@' 符号位置（从光标开始往前找）
        const beforeText = text.slice(0, offset)
        const atIndex = beforeText.lastIndexOf('@')
        if (atIndex !== -1) {
          // 取 @ 后面到光标的文本
          const searchText = beforeText.slice(atIndex + 1)
          // 只要存在 '@'，就保持弹窗打开
          setSuggestionsType('@')
          // 计算触发点位置(就是 '@' 的位置)
          const triggerPoint = { path, offset: atIndex }
          const range = { anchor: triggerPoint, focus: triggerPoint }
          const domRange = ReactEditor.toDOMRange(editor, range)
          const rect = domRange.getBoundingClientRect()
          handleMenuPosition(rect, '@')
          /* 搜索限制3个字符 交互同企业微信 */
          if (searchText.length > 2) {
            setSearchName('')
            setOpen(false)
            return
          }
          setOpen(true)
          setSearchName(searchText)
          return
        }
        setOpen(false)
      } catch (e) {
        setOpen(false)
        console.error('Error resolving DOM range:', e)
      }
    }

    /**
     * 方向键越过 mention 整块：
     * - 在 mention 左/右侧一键跳过整个节点
     */
    /**
     * 使用方向键跳过整块 mention
     * @param direction 跳过方向（left/right）
     * @returns 是否已处理跳过
     */
    function skipWholeBlock(direction: 'left' | 'right') {
      const { selection } = editor
      // 如果当前没有选区，或者选区不是折叠状态（即光标非单点），则无法跳过，直接返回 false
      if (!selection || !Range.isCollapsed(selection)) return false

      const point = selection.anchor // 当前光标位置（锚点）

      // 计算光标附近的点（根据方向左/右）
      const nearbyPoint =
        direction === 'right'
          ? Editor.after(editor, point, { unit: 'offset' }) // 光标右侧的一个字符位置
          : Editor.before(editor, point, { unit: 'offset' }) // 光标左侧的一个字符位置

      // 如果附近没有点（已到文本边界），返回 false
      if (!nearbyPoint) return false

      // 在附近点位置查找类型为 'mention' 的元素节点
      const mentionEntry = Array.from(
        Editor.nodes(editor, {
          at: nearbyPoint, // 搜索范围为附近点
          match: n => SlateElement.isElement(n) && (n as any).type === 'mention', // 匹配 mention 元素节点
          mode: 'lowest' // 查找最底层节点
        })
      )[0] // 取第一个匹配结果

      if (mentionEntry) {
        const [, mentionPath] = mentionEntry // 提取匹配到的节点路径

        // 计算跳过该 mention 后光标的新位置（根据方向）
        const targetPoint =
          direction === 'right' ? Editor.after(editor, mentionPath) : Editor.before(editor, mentionPath)

        if (targetPoint) {
          // 选中并聚焦跳过后的目标位置，实现光标跳过整个 mention 节点
          Transforms.select(editor, {
            anchor: targetPoint,
            focus: targetPoint
          })
          ReactEditor.focus(editor)
          return true // 成功跳过，返回 true
        }
      }

      return false // 未找到可跳过的 mention，返回 false
    }

    /**
     * 插入 mention 节点并将光标定位到其后
     */
    /**
     * 插入 mention 节点
     * @param editor Slate Editor 实例
     * @param mention { id: string; key?: string; name: string }
     */
    const insertMention = (editor, mention) => {
      // 构造一个 mention 节点对象
      const mentionNode = {
        type: 'mention', // 节点类型为 mention，方便后续识别和渲染
        name: mention.name, // mention 的显示名称
        id: mention.id || mention.key, // 使用 id 或 key
        key: mention.key, // 保存 key
        inline: true, // 设置为行内元素
        children: [{ text: '' }] // mention 节点必须包含 children，且这里是空文本节点
      }

      // 如果当前没有选区，直接返回，不执行插入
      if (!editor.selection) return

      // 在光标位置插入 mention 节点
      Transforms.insertNodes(editor, mentionNode)

      // 插入后，获取插入节点结束位置的光标点，即 mention 节点后面的位置
      const pointAfter = Editor.after(editor, editor.selection.focus)
      if (pointAfter) {
        // 如果成功获取到结束点，设置光标到 mention 节点之后
        Transforms.select(editor, { anchor: pointAfter, focus: pointAfter })
        ReactEditor.focus(editor) // 聚焦编辑器，保证光标生效
      } else {
        // 备用方案：根据插入的 mention 节点，找到其路径，设置光标到节点末尾
        const path = ReactEditor.findPath(editor, mentionNode)
        const end = Editor.end(editor, path)
        Transforms.select(editor, { anchor: end, focus: end })
        ReactEditor.focus(editor)
      }
    }

    /* 点击蒙层 光标切换 插入建议项*/
    /**
     * 选择建议项：删除 @... 输入，插入 mention；支持多选插入
     */
    /**
     * 选择建议项并插入 mention
     * @param items 单个或数组形式的建议项（需包含 key 与 title）
     */
    const insertSuggestion = (items: Object | Array<object> | any) => {
      const editor = editorRef.current
      if (!editor) return
      // 标准化为数组
      const itemList = Array.isArray(items) ? items : [items]
      // 获取当前选择
      const { selection } = editor
      if (!selection) return

      // 找到当前光标位置前的 @ 符号
      const [node, path] = Editor.node(editor, selection.anchor)
      if (!Text.isText(node)) return

      const text = node.text
      const offset = selection.anchor.offset
      const beforeText = text.slice(0, offset)
      const atIndex = beforeText.lastIndexOf('@')

      if (atIndex !== -1) {
        // 删除从 @ 到当前光标位置的所有文本
        Transforms.delete(editor, {
          at: {
            anchor: { path, offset: atIndex },
            focus: { path, offset }
          }
        })
      }

      // 插入新的 mention
      for (const item of itemList) {
        insertMention(editor, { id: item.key, name: item.title })
      }
      // 插入后滚动到光标位置
      setTimeout(() => {
        try {
          const domRange = ReactEditor.toDOMRange(editor, editor.selection!)
          domRange.endContainer.parentElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          })
        } catch (e) {
          console.warn('Scroll to selection failed:', e)
        }
      }, 0)
      /* 关闭list弹窗 */
      setOpen(false)
    }

    /**
     * mention 节点渲染：点击时将光标移动到其后，便于继续输入
     */
    /**
     * mention 行内节点渲染
     * @param attributes Slate 传入的节点属性
     * @param children 子节点
     * @param element mention 元素（包含 name/id）
     */
    const Mention = ({ attributes, children, element }) => {
      const editor = useSlate() as any // 从 Slate 的 context 中获取当前编辑器实例

      // 点击 mention 节点时的处理函数
      const handleOnClick = e => {
        e.preventDefault()
        e.stopPropagation() // 阻止事件冒泡

        // 获取当前 mention 节点在文档中的路径
        const path = ReactEditor.findPath(editor, element)

        // 获取 mention 节点后的位置
        const after = Editor.after(editor, path)
        if (after) {
          // 将光标设置到 mention 节点后面
          Transforms.select(editor, after)
          ReactEditor.focus(editor)
        } else {
          // 如果没有后续位置，则移动到下一个节点
          const nextPath = [...path]
          nextPath[nextPath.length - 1] += 1
          if (Editor.hasPath(editor, nextPath)) {
            Transforms.select(editor, { anchor: { path: nextPath, offset: 0 }, focus: { path: nextPath, offset: 0 } })
            ReactEditor.focus(editor)
          }
        }
      }

      return (
        <span {...attributes} contentEditable={false} onClick={handleOnClick} className="atSendMessage">
          @{element.name}
        </span>
      )
    }

    // 自定义元素渲染：mention 与普通段落
    const renderElement = useCallback(props => {
      if (props.element.type === 'mention') {
        return <Mention {...props} />
      }
      // 其他类型
      return <p {...props.attributes}>{props.children}</p>
    }, [])

    // 自定义叶子节点渲染，给普通文本添加样式
    // 自定义文本渲染：统一样式包裹
    const renderLeaf = useCallback(props => {
      return (
        <span {...props.attributes} className="normalTextSpan">
          {props.children}
        </span>
      )
    }, [])

    const editableContentRef = useRef(null) // 创建一个 ref 用于引用 modal-content-wrapper
    useEffect(() => {
      // 当 messages 发生变化时，滚动到底部
      if (editableContentRef.current) {
        editableContentRef.current.scrollTop = editableContentRef.current.scrollHeight
      }
    }, [value]) // 监听 messages 的变化

    /* 点击focus时 光标移动到最后 - 会话编辑回显需要使用 */
    /**
     * 焦点进入：确保末尾可见光标。
     * 若末尾是 mention，则追加空文本节点以显示光标。
     */
    function onFocusHandler(editor: any) {
      setTimeout(() => {
        // 先判断是否有内容
        if (editor.children.length === 0) {
          // 如果内容为空，直接插入一个空文本节点，避免报错
          Transforms.insertNodes(editor, { text: '' })
          ReactEditor.focus(editor)
          return
        }
        // 有内容时再执行
        const endPoint = Editor.end(editor, [])
        const [node, path] = Editor.node(editor, endPoint)

        const [parentNode, parentPath] = Editor.parent(editor, path)

        const lastChild = parentNode.children[parentNode.children.length - 1] as any

        // 如果最后一个是 mention，则插入一个空文本节点（让光标能"显示"出来）
        if (!Text.isText(lastChild) && lastChild.type === 'mention') {
          const insertPath = [...parentPath, parentNode.children.length]
          Transforms.insertNodes(editor, { text: '' }, { at: insertPath })
          Transforms.select(editor, {
            anchor: { path: insertPath, offset: 0 },
            focus: { path: insertPath, offset: 0 }
          })
        } else {
          // 否则直接选中最后
          Transforms.select(editor, {
            anchor: endPoint,
            focus: endPoint
          })
        }

        ReactEditor.focus(editor)
      }, 300)
    }

    // 修改键盘事件处理逻辑
    /**
     * 键盘事件：
     * - 打开建议时将按键事件转发到建议列表
     * - Enter 发送（建议关闭时）
     * - 方向键尝试越过 mention 块
     */
    /**
     * 键盘事件处理
     * @param e KeyboardEvent
     */
    const handleKeyDown = (e: React.KeyboardEvent) => {
      // 记录最后按下的键
      lastKeyRef.current = e.key

      // 处理左右箭头跳过整个块
      if (e.key === 'ArrowRight') {
        const didSkip = skipWholeBlock('right')
        if (didSkip) {
          e.preventDefault()
          return
        }
      }

      if (e.key === 'ArrowLeft') {
        const didSkip = skipWholeBlock('left')
        if (didSkip) {
          e.preventDefault()
          return
        }
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const handled = skipWholeBlock(e.key === 'ArrowLeft' ? 'left' : 'right')
        if (handled) {
          console.log('Skip whole block handled:', e.key)
          e.preventDefault()
        }
      }

      // 处理建议列表打开时的键盘事件
      if (open) {
        if (
          e.key === 'ArrowDown' ||
          e.key === 'ArrowUp' ||
          e.key === 'Enter' ||
          e.key === 'Escape' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight'
        ) {
          e.preventDefault()
          // 将键盘事件传递给MentionSuggestions组件
          const event = new KeyboardEvent('keydown', {
            key: e.key,
            code: e.code,
            keyCode: e.keyCode,
            which: e.which,
            bubbles: true
          })
          const mentionSuggestions = document.querySelector('.sendCase')
          if (mentionSuggestions) {
            mentionSuggestions.dispatchEvent(event)
          }
        }
      } else {
        // 处理建议列表关闭时的Enter键
        if (e.key === 'Enter') {
          e.preventDefault()
          setplacholderType(true)
          enterFun()
        }
      }
    }

    return (
      <div ref={editableContentRef} className={`relative h-[100%] w-[100%] ${disabled ? 'cursor-not-allowed' : ''}`}>
        {/* 伪 placeholder */}
        {placholderType && (
          <div
            className={`Editable_textarea_plachoder pointer-events-none select-none ${containsFile ? 'pt-[62px]' : ''}`}
          >
            {placeholder}
          </div>
        )}
        <Slate editor={editor} initialValue={value} onChange={handleChange}>
          <Editable
            readOnly={disabled}
            className={`Editable_textarea caseOverFlowY h-[100%] w-[100%] bg-[#fafbfc] p-[16px] ${
              containsFile ? 'pt-[78px]' : ''
            }`}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            /* 键盘判断 */
            onKeyDown={handleKeyDown}
            /* 点击判断 */
            onMouseDown={() => {
              onInputClick && onInputClick()
            }}
            onFocus={() => onFocusHandler(editor)}
            /* 中文输入法判断 */
            onCompositionStart={() => {
              setplacholderType(false)
            }}
            onCompositionEnd={() => {
              /* 判断文字内容 */
              setplacholderType(sendValue ? false : true)
            }}
            /* 拖拽事件处理 */
            onDrop={(e) => {
              // 阻止默认行为，防止在编辑器中插入文件内容
              e.preventDefault()
              // 不阻止事件冒泡，让容器级别处理上传
            }}
            onDragOver={(e) => {
              // 阻止默认行为，允许拖拽
              e.preventDefault()
            }}
          />
          {
            /* 判断是否需要显示建议列表，检查是否为 '{' 类型 */
            open && suggestionsType === '@' && (
              /* 根据 suggestionsType 为 '@' 来渲染 BracketCurlySuggestions 组件 */
              <MentionSuggestions
                searchName={searchName}
                menuPosition={menuPosition} // 传递菜单的位置
                suggestionRefs={suggestionRefs} // 传递建议项的引用（用于高亮显示等）
                isKeyboardNavigating={isKeyboardNavigating} // 是否正在通过键盘进行导航
                insertSuggestion={insertSuggestion} // 插入建议的回调函数
                setIsKeyboardNavigating={setIsKeyboardNavigating} // 设置键盘导航状态的回调
                resetFun={() => {
                  setOpen(false)
                  setSearchName('')
                }}
              />
            )
          }
        </Slate>
      </div>
    )
  }
)

export default ChatSendInput
